/*
 * Ryan O'Dowd
 * 2025-07-18
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import Banner from '@components/common/Banner';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import Table from '@components/common/Table';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import Tooltip from '@components/common/Tooltip';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';

const ManageSeatsDialog = (props: ManageSeatsDialogProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();

  const [_numSeatsToAddText, setNumSeatsToAddText] = useState('1');

  const _getHumanReadableSeatRange = (firstSeat, lastSeat) => {
    if (firstSeat === lastSeat) {
      return `seat ${firstSeat}`;
    }

    return `seats ${firstSeat}-${lastSeat}`;
  };

  const seatsTableRows: any = [
    [
      {
        id: 'first_100_item',
        content: `Current seats (${_getHumanReadableSeatRange(1, Math.min(props.school.billing.total_seats_paid_for_in_current_subscription, 100))})`,
      },
      {
        id: 'first_100_number_of_seats',
        content: Math.min(props.school.billing.total_seats_paid_for_in_current_subscription, 100),
      },
      {
        id: 'first_100_cost_per_seat',
        content: '$10', // @TODO: don't hardcode this
      },
      {
        id: 'first_100_total_cost',
        content: `$${Math.min(100, props.school.billing.total_seats_paid_for_in_current_subscription) * 10}`, // @TODO: don't hardcode 10
      },
    ],
  ];

  if (props.school.billing.total_seats_paid_for_in_current_subscription > 100) {
    seatsTableRows.push([
      {
        id: 'over_100_item',
        content: `Next renewal seats (${_getHumanReadableSeatRange(101, props.school.billing.total_seats_paid_for_in_current_subscription)})`, // @TODO: should 101 be hardcoded?
      },
      {
        id: 'over_100_number_of_seats',
        content: props.school.billing.total_seats_paid_for_in_current_subscription - 100, // @TODO: don't hardcode 100
      },
      {
        id: 'over_100_cost_per_seat',
        content: '$8', // @TODO: don't hardcode this
      },
      {
        id: 'over_100_total_cost',
        content: `$${(props.school.billing.total_seats_paid_for_in_current_subscription - 100) * 8}`, // @TODO: don't hardcode 100 and 8
      },
    ]);
  }

  const invoicesTableRows = props.school.billing.transactions.map((transaction) => {
    return [
      {
        id: `${transaction.id}_id`,
        content: <Text>{`${props.school.id}`.padStart(4, '0')}-{`${transaction.id}`.padStart(5, '0')}</Text>, // @TODO: should probably have a transaction id, but i don't like using the db column id
        sortValue: +transaction.id,
      },
      {
        id: `${transaction.id}_created_at`,
        content: <Tooltip title={dateFnsFormat(transaction.created_at, 'hh:mm a')}><Text>{dateFnsFormat(new Date(transaction.created_at), 'MMMM d, yyyy')}</Text></Tooltip>,
        sortValue: new Date(transaction.created_at),
      },
      {
        id: `${transaction.id}_number_of_seats`,
        content: <Text>{`${transaction.number_of_seats} ${transaction.number_of_seats === 1 ? 'seat' : 'seats'} ${transaction.number_of_seats < 0 ? 'removed' : 'added'}`}</Text>,
        sortValue: +transaction.number_of_seats,
      },
      {
        id: `${transaction.id}_cost`,
        content: <Text>{`$${transaction.total_cost_of_seats}`}</Text>,
        sortValue: +transaction.total_cost_of_seats,
      },
      // @TODO: pdf download link
    ];
  });


  let numSeatsToAddUnder100 = 0;
  const numSeatsToAdd = +_numSeatsToAddText;
  if (props.school.billing.total_seats_paid_for_in_current_subscription < 100) {
    numSeatsToAddUnder100 = Math.min(100 - props.school.billing.total_seats_paid_for_in_current_subscription, numSeatsToAdd);
    seatsTableRows.push([
      {
        id: 'new_under_100_item',
        content: `New seats (${_getHumanReadableSeatRange(props.school.billing.total_seats_paid_for_in_current_subscription + 1, props.school.billing.total_seats_paid_for_in_current_subscription + numSeatsToAddUnder100)})`,
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_under_100_number_of_seats',
        content: numSeatsToAddUnder100,
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_under_100_cost_per_seat',
        content: '$10', // @TODO: don't hardcode this
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_under_100_total_cost',
        content: `$${numSeatsToAddUnder100 * 10}${props.isInTrial ? '' : ' (due today)'}`, // @TODO: don't hardcode 10
        textStyles: styles.currentRowText,
      },
    ]);
  }

  let numSeatsToAddOver100 = 0; // @TODO: update name to reflect new tiered pricing
  if ((props.school.billing.total_seats_paid_for_in_current_subscription + numSeatsToAdd) > 100) {
    numSeatsToAddOver100 = numSeatsToAdd - numSeatsToAddUnder100;
    seatsTableRows.push([
      {
        id: 'new_over_100_item',
        content: `New seats (${_getHumanReadableSeatRange(props.school.billing.total_seats_paid_for_in_current_subscription + numSeatsToAddUnder100 + 1, props.school.billing.total_seats_paid_for_in_current_subscription + numSeatsToAddUnder100 + numSeatsToAddOver100)})`,
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_over_100_number_of_seats',
        content: numSeatsToAddOver100,
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_over_100_cost_per_seat',
        content: '$8', // @TODO: don't hardcode this
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_over_100_total_cost',
        content: `$${numSeatsToAddOver100 * 8}${props.isInTrial ? '' : ' (due today)'}`, // @TODO: don't hardcode 8
        textStyles: styles.currentRowText,
      },
    ]);
  }

  const totalsRows: any = [
    [
      {
        id: 'todays_total_item',
        content: "Today's total",
        textStyles: [styles.currentRowText, styles.totalsRowText],
      },
      {
        id: 'todays_total_number_of_seats',
        content: numSeatsToAdd,
        textStyles: [styles.currentRowText, styles.totalsRowText],
      },
      {
        id: 'todays_total_cost_per_seat',
        content: '',
        textStyles: [styles.currentRowText, styles.totalsRowText],
      },
      {
        id: 'todays_total_total_cost',
        content: `$${
          (!props.isInTrial ? 0 : (props.school.billing.total_seats_paid_for_in_current_subscription * 10)) + // @TODO: don't hardcode 10...also, not all current seats are guaranteed to be the same price
          (numSeatsToAddUnder100 * 10) + // @TODO: don't hardcode 10
          (numSeatsToAddOver100 * 8) // @TODO: don't hardcode 100 and 8
        }`,
        textStyles: [styles.currentRowText, styles.totalsRowText],
      },
    ],
  ];

  if (!props.isInTrial) {
    totalsRows.push([
      {
        id: 'grand_total_item',
        content: `Total due at next renewal (${dateFnsFormat(new Date(props.school.billing.renewal_date), 'MMMM d, yyyy')})`, // @TODO: if currently in trial, this should be one year from today
      },
      {
        id: 'grand_total_number_of_seats',
        content: props.school.billing.total_seats_paid_for_in_current_subscription + numSeatsToAdd,
      },
      {
        id: 'grand_total_cost_per_seat',
        content: '',
      },
      {
        id: 'grand_total_total_cost',
        content: `$${
          ((Math.min(props.school.billing.total_seats_paid_for_in_current_subscription, 100) + numSeatsToAddUnder100) * 10) + // @TODO: don't hardcode 10 and 100
          ((Math.max(0, Math.min(props.school.billing.total_seats_paid_for_in_current_subscription - 100)) + numSeatsToAddOver100) * 8) // @TODO: don't hardcode 100 and 8
        }`,
      },
    ]);
  }

  return (
    <Dialog
      visible={true}
      onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
    >
      <Dialog.Title>Add seats</Dialog.Title>{/* @TODO: i18n */}
      <Dialog.Content>
        {props.isInTrial &&
          <span style={styles.publishedStatusWrapper}>
            <Banner
              type='info'
              text='Your subscription will begin upon checkout.'// @TODO: wordsmith this
            />
          </span>
        }
        <TextInput
          mode='outlined'
          value={`${numSeatsToAdd}`}
          onChangeText={(text) => setNumSeatsToAddText(text.replace(/[^0-9]/g, ''))}// @TODO: should numbersOnly be a prop?
          label='Number of seats to add'
        />
        <Table
          tableTitle='All seats'
          open={true}
          disableAccordion={true}
          headerRowData={[
            {width: 220, id: 'type', content: 'Item'}, // @TODO: better name than item
            {width: 100, id: 'number_of_seats', content: '# seats', rightAlign: true},
            {width: 100, id: 'cost_per_seat', content: 'Cost per seat', rightAlign: true},
            {width: 100, id: 'total_cost', content: 'Total', rightAlign: true},
          ]}
          isSortable={false}
          rowData={seatsTableRows}// @TODO: shouldn't be clickable (or if they are, make individually updatable)
          totalsRowsData={totalsRows}// @TODO: shouldn't be clickable (or if they are, make individually updatable)
        />

        <Table
          tableTitle='Past invoices'
          open={false}
          headerRowData={[
            {width: 50, id: 'id', content: 'Transaction ID'},
            {width: 70, id: 'created_at', content: 'Date'},
            {width: 70, id: 'number_of_seats', content: '# seats', rightAlign: true},
            {width: 70, id: 'total_cost_of_seats', content: 'Amount', rightAlign: true},
            // @TODO: pdf download
          ]}
          isSortable={true}
          orderByIndex={0}
          rowData={invoicesTableRows}// @TODO: shouldn't be clickable (or if they are, make individually updatable)
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          mode='text'
          onPress={() => props.onClose()}
        >
          {i18n('cancel')}
        </Button>
        <Button
          onPress={() => {
            // @TODO: take to stripe, save to db after success (stripe_subscription_id, stripe_payment_method_id, total_seats_paid_for_in_current_subscription)
            // @TODO: set the trial_period_ends_at to now
            // @TODO: set the renewal_date to one year from now

            // @TODO: create record in transactions table
          }}
        >{/* @TODO: i18n */}
          Checkout
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class ManageSeatsDialogProps {
  onClose: () => void;
  school: any;
  isInTrial: boolean;
};

export default ManageSeatsDialog;
